import React, { useState } from 'react';
import { content } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Check, Sparkles, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import PricingCalculator from './PricingCalculator';

const Pricing = () => {
  const { language } = useLanguage();
  const t = content[language]?.pricing || content.en.pricing;
  const [showCalculator, setShowCalculator] = useState(false);

  const openCalculator = () => {
    setShowCalculator(true);
  };

  return (
    <section id="pricing" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-orange-50 text-orange-600 font-medium text-sm mb-4">
            {t.subtitle}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {t.title}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t.description}
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {t.routes.map((route, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${
                index === 0 ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {/* Popular badge */}
              {index === 0 && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  {language === 'fr' ? 'Plus populaire' : 'Most Popular'}
                </div>
              )}

              {/* Header */}
              <h3 className="text-xl font-bold text-slate-900 mb-4 mt-2">
                {route.title}
              </h3>

              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">
                  {route.price}
                </span>
                <span className="text-slate-500 ml-2">
                  {route.unit}
                </span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {route.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                onClick={openCalculator}
                className={`w-full ${
                  index === 0 
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                }`}
              >
                <Calculator className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Calculer le prix' : 'Calculate Price'}
              </Button>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="text-center">
          <p className="text-slate-500 text-sm max-w-2xl mx-auto">
            {t.note}
          </p>
        </div>

        {/* Additional delivery info */}
        <div className="mt-16 grid md:grid-cols-4 gap-6">
          {[
            { 
              label: language === 'fr' ? 'Express Aérien' : 'Air Express', 
              value: '3-7', 
              unit: language === 'fr' ? 'jours' : 'days',
              color: 'blue' 
            },
            { 
              label: language === 'fr' ? 'Standard' : 'Standard', 
              value: '4-10', 
              unit: language === 'fr' ? 'jours' : 'days',
              color: 'cyan' 
            },
            { 
              label: language === 'fr' ? 'Livraison locale' : 'Local Delivery', 
              value: '24-72', 
              unit: language === 'fr' ? 'heures' : 'hours',
              color: 'orange' 
            },
            { 
              label: language === 'fr' ? 'Maritime' : 'Sea Freight', 
              value: '4-7', 
              unit: language === 'fr' ? 'semaines' : 'weeks',
              color: 'slate' 
            }
          ].map((item, index) => (
            <div key={index} className="text-center p-6 rounded-2xl bg-white shadow-md">
              <div className={`text-3xl font-bold mb-1 ${
                item.color === 'blue' ? 'text-blue-600' :
                item.color === 'cyan' ? 'text-cyan-600' :
                item.color === 'orange' ? 'text-orange-600' : 'text-slate-600'
              }`}>
                {item.value}
              </div>
              <div className="text-slate-500 text-sm">{item.unit}</div>
              <div className="text-slate-900 font-medium mt-1">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Calculator Modal */}
      <PricingCalculator 
        isOpen={showCalculator} 
        onClose={() => setShowCalculator(false)} 
      />
    </section>
  );
};

export default Pricing;
